'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCartPlus } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { decodeEntities } from '@/app/_lib/services/ClientUtil';
import Price from '@/app/_components/Price';
import ProductFavoriteMarker from '@/app/_components/ProductFavoriteMarker';
import { useGlobalMessage } from '@/app/_lib/hooks';
import { useConfirmDialog } from '@/app/_lib/_context';

// Format unix timestamp to readable date
const formatBackorderDate = timestamp => {
  if (!timestamp) return null;
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
};
const CartOptions = t0 => {
  const $ = _c(2);
  const {
    options
  } = t0;
  if (!options) {
    return null;
  }
  const now = Date.now() / 1000;
  let t1;
  if ($[0] !== options) {
    t1 = <>{Object.entries(options).map((option, index) => {
        const opt = option[1];
        let optionBackorderMessage = null;
        if (opt.production_hold_expiration_date && opt.production_hold_expiration_date > now) {
          optionBackorderMessage = {
            prompt: "production hold",
            message: `estimated availability ${formatBackorderDate(opt.production_hold_expiration_date)}`
          };
        } else {
          if (opt.backorder_expiration_date && opt.backorder_expiration_date > now) {
            optionBackorderMessage = {
              prompt: "backordered",
              message: `estimated in stock ${formatBackorderDate(opt.backorder_expiration_date)}`
            };
          }
        }
        return <div key={index} className="font-medium capitalize mb-2 text-sm"><label className="font-extralight text mr-1">{opt.selection_label}:</label><span className="text-light">{opt.option_name}</span>{opt.option_price > 0 && <span className="font-medium text-primary">+ ${opt.option_price}</span>}{optionBackorderMessage && <div className="text-red text-xs mt-1"><span className="font-medium">{optionBackorderMessage.prompt}</span> {optionBackorderMessage.message}</div>}</div>;
      })}</>;
    $[0] = options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const CartSwatches = t0 => {
  const $ = _c(2);
  const {
    swatches
  } = t0;
  if (!swatches) {
    return null;
  }
  const now = Date.now() / 1000;
  let t1;
  if ($[0] !== swatches) {
    t1 = <>{Object.entries(swatches).map((swatch, index) => {
        const sw = swatch[1];
        let swatchBackorderMessage = null;
        if (sw.production_hold_expiration_date && sw.production_hold_expiration_date > now) {
          swatchBackorderMessage = {
            prompt: "production hold",
            message: `estimated availability ${formatBackorderDate(sw.production_hold_expiration_date)}`
          };
        } else {
          if (sw.backorder_expiration_date && sw.backorder_expiration_date > now) {
            swatchBackorderMessage = {
              prompt: "backordered",
              message: `estimated in stock ${formatBackorderDate(sw.backorder_expiration_date)}`
            };
          }
        }
        return <div key={index} className="font-medium capitalize mb-2 text-sm"><label className="font-extralight text-light mr-1">{sw.swatch_set_label}:</label><span className="text-light">{sw.swatch_name}{sw.swatch_code && <span className="text-extra-light ml-0.5">({sw.swatch_code})</span>}</span>{sw.price > 0 && <span className="text-primary ml-1">+ <Price amount={sw.price} /></span>}{sw.swatch_price > 0 && <span className="text-primary ml-1">+ <Price amount={sw.swatch_price} /></span>}{swatchBackorderMessage && <div className="text-red text-xs mt-1"><span className="font-medium">{swatchBackorderMessage.prompt}</span> {swatchBackorderMessage.message}</div>}</div>;
      })}</>;
    $[0] = swatches;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const SavedItem = t0 => {
  const $ = _c(110);
  const {
    savedItem,
    itemIndex,
    deleteSavedItem,
    moveSavedItemToCart
  } = t0;
  const addGlobalMessage = useGlobalMessage();
  const {
    confirm
  } = useConfirmDialog();
  const getImageForItem = _temp;
  let t1;
  if ($[0] !== addGlobalMessage || $[1] !== confirm || $[2] !== deleteSavedItem) {
    t1 = async (event, itemIndex_0, savedItem_1) => {
      const result = await confirm({
        title: "Remove Saved Item?",
        message: `Are you sure you want to remove "${decodeEntities(savedItem_1.product.sub_product_name)}" from your saved items?`,
        buttons: [{
          label: "Remove",
          action: "remove",
          variant: "primary"
        }]
      });
      if (result === "remove") {
        deleteSavedItem(itemIndex_0);
        addGlobalMessage({
          message: "Item removed from saved items",
          messageType: "success"
        });
      }
    };
    $[0] = addGlobalMessage;
    $[1] = confirm;
    $[2] = deleteSavedItem;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleDeleteSavedItemClick = t1;
  let t2;
  if ($[4] !== addGlobalMessage || $[5] !== moveSavedItemToCart) {
    t2 = async (event_0, itemIndex_1, savedItem_2) => {
      await moveSavedItemToCart(itemIndex_1);
      const message = decodeEntities(savedItem_2.product.sub_product_name) + " was moved to your cart.";
      addGlobalMessage({
        message,
        messageType: "success"
      });
    };
    $[4] = addGlobalMessage;
    $[5] = moveSavedItemToCart;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleMoveToCartClick = t2;
  const t3 = `${savedItem.product?.url}`;
  let t4;
  if ($[7] !== savedItem.product.sub_product_name) {
    t4 = decodeEntities(savedItem.product.sub_product_name);
    $[7] = savedItem.product.sub_product_name;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== savedItem) {
    t5 = getImageForItem(savedItem);
    $[9] = savedItem;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = `${process.env.NEXT_PUBLIC_RESOURCE_URL}/${t5}`;
  let t7;
  if ($[11] !== savedItem.product.sub_product_name) {
    t7 = decodeEntities(savedItem.product.sub_product_name);
    $[11] = savedItem.product.sub_product_name;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      objectFit: "contain"
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t7) {
    t9 = <Image src={t6} alt={t7} className="max-w-[150px] h-auto p-4" style={t8} fill={true} quality={90} sizes="150px" />;
    $[14] = t6;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t9) {
    t10 = <div className="col-span-1 h-full text-center"><Link href={t3} title={t4} className="bg-white block relative w-full max-w-[150px] aspect-square text-center overlay-block mx-auto">{t9}</Link></div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = `${savedItem.product?.url}`;
  let t12;
  if ($[21] !== savedItem.product.sub_product_name) {
    t12 = decodeEntities(savedItem.product.sub_product_name);
    $[21] = savedItem.product.sub_product_name;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== savedItem.product.sub_product_name) {
    t13 = decodeEntities(savedItem.product.sub_product_name);
    $[23] = savedItem.product.sub_product_name;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t13) {
    t14 = <Link href={t11} title={t12} className="block font-medium capitalize text-md mr-2">{t13}</Link>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = savedItem.product?.sub_product_id;
  let t16;
  if ($[29] !== t15) {
    t16 = <ProductFavoriteMarker sub_product_id={t15} isVisible={true} className="p-0" />;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t14 || $[32] !== t16) {
    t17 = <div className="flex items-center mb-2">{t14}{t16}</div>;
    $[31] = t14;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== savedItem.product.backorder_hold_message || $[35] !== savedItem.product.backorder_message || $[36] !== savedItem.product.backordered || $[37] !== savedItem.product.custom_backorder_message || $[38] !== savedItem.product.in_inventory) {
    t18 = savedItem.product?.custom_backorder_message ? <div className="text-red text-xs mb-2"><span className="font-medium">{savedItem.product.backorder_hold_message}</span> {savedItem.product.backorder_message}</div> : savedItem.product?.backordered && !savedItem.product?.in_inventory && savedItem.product?.backorder_message && <div className="text-red text-xs mb-2"><span className="font-medium">backordered</span>, {savedItem.product.backorder_message}</div>;
    $[34] = savedItem.product.backorder_hold_message;
    $[35] = savedItem.product.backorder_message;
    $[36] = savedItem.product.backordered;
    $[37] = savedItem.product.custom_backorder_message;
    $[38] = savedItem.product.in_inventory;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== savedItem.product.options) {
    t19 = <CartOptions options={savedItem.product.options} />;
    $[40] = savedItem.product.options;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== savedItem.product.swatches) {
    t20 = <CartSwatches swatches={savedItem.product.swatches} />;
    $[42] = savedItem.product.swatches;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <label className="font-light text-light me-1">Price:</label>;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = savedItem.product?.retail_price;
  let t23;
  if ($[45] !== t22) {
    t23 = <div className="mb-2 text-sm">{t21}<Price amount={t22} className="font-medium" /></div>;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <label className="lg:hidden font-light text-light me-1">Shipping:</label>;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  const t25 = savedItem.shipping?.cart_message;
  let t26;
  if ($[48] !== t25) {
    t26 = <div className="lg:hidden text-sm font-medium mb-2 lg:mb-0">{t24}<span className="font-medium">{t25}</span></div>;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== savedItem.product.lead_time) {
    t27 = savedItem.product?.lead_time && <div className="mb-2 text-sm capitalize"><span className="text-light">{savedItem.product.lead_time}</span></div>;
    $[50] = savedItem.product.lead_time;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <label className="lg:hidden font-light text-light me-1">Total:</label>;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== savedItem.total_price) {
    t29 = <div className="lg:hidden w-full text-sm">{t28}<Price amount={savedItem.total_price} className="font-medium" /></div>;
    $[53] = savedItem.total_price;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== handleMoveToCartClick || $[56] !== itemIndex || $[57] !== savedItem) {
    t30 = event_1 => handleMoveToCartClick(event_1, itemIndex, savedItem);
    $[55] = handleMoveToCartClick;
    $[56] = itemIndex;
    $[57] = savedItem;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  let t32;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <span className="mr-1">Move to Cart</span>;
    t32 = <FontAwesomeIcon icon={faCartPlus} />;
    $[59] = t31;
    $[60] = t32;
  } else {
    t31 = $[59];
    t32 = $[60];
  }
  let t33;
  if ($[61] !== t30) {
    t33 = <button onClick={t30} title="Move to Cart" className="block text-2xs mb-3 w-[150px] flex items-center">{t31}{t32}</button>;
    $[61] = t30;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] !== handleDeleteSavedItemClick || $[64] !== itemIndex || $[65] !== savedItem) {
    t34 = event_2 => handleDeleteSavedItemClick(event_2, itemIndex, savedItem);
    $[63] = handleDeleteSavedItemClick;
    $[64] = itemIndex;
    $[65] = savedItem;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  let t36;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <span className="mr-1">Remove</span>;
    t36 = <FontAwesomeIcon icon={faTrash} />;
    $[67] = t35;
    $[68] = t36;
  } else {
    t35 = $[67];
    t36 = $[68];
  }
  let t37;
  if ($[69] !== t34) {
    t37 = <button onClick={t34} title="Remove From Saved Items" className="text-2xs mb-3 w-[150px] flex items-center">{t35}{t36}</button>;
    $[69] = t34;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] !== t33 || $[72] !== t37) {
    t38 = <div className="block md:hidden mt-3">{t33}{t37}</div>;
    $[71] = t33;
    $[72] = t37;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t17 || $[75] !== t18 || $[76] !== t19 || $[77] !== t20 || $[78] !== t23 || $[79] !== t26 || $[80] !== t27 || $[81] !== t29 || $[82] !== t38) {
    t39 = <div className="col-span-1 lg:col-span-2">{t17}{t18}{t19}{t20}{t23}{t26}{t27}{t29}{t38}</div>;
    $[74] = t17;
    $[75] = t18;
    $[76] = t19;
    $[77] = t20;
    $[78] = t23;
    $[79] = t26;
    $[80] = t27;
    $[81] = t29;
    $[82] = t38;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  let t40;
  if ($[84] !== savedItem.total_price) {
    t40 = <div className="text-sm text-light font-medium mb-3"><Price amount={savedItem.total_price} /></div>;
    $[84] = savedItem.total_price;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  let t41;
  if ($[86] !== handleMoveToCartClick || $[87] !== itemIndex || $[88] !== savedItem) {
    t41 = event_3 => handleMoveToCartClick(event_3, itemIndex, savedItem);
    $[86] = handleMoveToCartClick;
    $[87] = itemIndex;
    $[88] = savedItem;
    $[89] = t41;
  } else {
    t41 = $[89];
  }
  let t42;
  let t43;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <span className="mr-1">Move to Cart</span>;
    t43 = <FontAwesomeIcon icon={faCartPlus} />;
    $[90] = t42;
    $[91] = t43;
  } else {
    t42 = $[90];
    t43 = $[91];
  }
  let t44;
  if ($[92] !== t41) {
    t44 = <button onClick={t41} title="Move to Cart" className="block text-2xs text-light mb-3 flex items-center">{t42}{t43}</button>;
    $[92] = t41;
    $[93] = t44;
  } else {
    t44 = $[93];
  }
  let t45;
  if ($[94] !== handleDeleteSavedItemClick || $[95] !== itemIndex || $[96] !== savedItem) {
    t45 = event_4 => handleDeleteSavedItemClick(event_4, itemIndex, savedItem);
    $[94] = handleDeleteSavedItemClick;
    $[95] = itemIndex;
    $[96] = savedItem;
    $[97] = t45;
  } else {
    t45 = $[97];
  }
  let t46;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <span className="mr-1">Remove</span>;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <FontAwesomeIcon icon={faTrash} />;
    $[99] = t47;
  } else {
    t47 = $[99];
  }
  let t48;
  if ($[100] !== t45) {
    t48 = <button onClick={t45} title="Remove From Saved Items" className="text-2xs text-light mb-3 flex items-center">{t46} {t47}</button>;
    $[100] = t45;
    $[101] = t48;
  } else {
    t48 = $[101];
  }
  let t49;
  if ($[102] !== t40 || $[103] !== t44 || $[104] !== t48) {
    t49 = <div className="hidden md:block md:col-span-1 lg:col-span-2 lg:col-start-5 text-right place-items-end">{t40}{t44}{t48}</div>;
    $[102] = t40;
    $[103] = t44;
    $[104] = t48;
    $[105] = t49;
  } else {
    t49 = $[105];
  }
  let t50;
  if ($[106] !== t10 || $[107] !== t39 || $[108] !== t49) {
    t50 = <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 mb-6 items-start min-h-40 w-full lg:w-2/3 2xl:w-3/4">{t10}{t39}{t49}</div>;
    $[106] = t10;
    $[107] = t39;
    $[108] = t49;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  return t50;
};
export default SavedItem;

/*fill={true}
style={{
    objectFit: 'contain', // cover, contain, none
}}*/
function _temp(savedItem_0) {
  let file_name;
  if (savedItem_0.product?.variant && savedItem_0.product?.variant.file_name) {
    file_name = savedItem_0.product.variant.file_name;
  } else {
    if (savedItem_0.product.thumb_1) {
      file_name = "/thumbs/" + savedItem_0.product.thumb_1;
    } else {
      file_name = savedItem_0.product.product_photo;
    }
  }
  return file_name;
}